import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Building2,
  CalendarDays,
  Check,
  ClipboardCheck,
  Headphones,
  Menu,
  Music2,
  SlidersHorizontal,
  Smartphone,
  UsersRound,
  X,
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import './Marketing.css';

const learningGroups = [
  {
    icon: BookOpen,
    title: '系统学习',
    features: '乐理学习 · 音乐简史 · 乐器百科',
    description: '从理解到练习，逐步建立音乐基础。',
  },
  {
    icon: Headphones,
    title: '基础训练',
    features: '识谱 · 指板 · 节奏 · 听音 · 声乐',
    description: '课后有方向，练习更专注。',
  },
  {
    icon: SlidersHorizontal,
    title: '实用工具',
    features: '节拍器 · 吉他调音',
    description: '做好练习准备，随时进入状态。',
  },
];

const management = [
  {
    icon: UsersRound,
    title: '招生与报名',
    description: '串联意向跟进、学员报名与课程信息，减少重复记录。',
  },
  {
    icon: CalendarDays,
    title: '排课与约课',
    description: '协调教师、教室与学员，清晰安排一对一及跨校区课程。',
  },
  {
    icon: ClipboardCheck,
    title: '上课与课消',
    description: '连接上课记录与课次变化，让每一次课程都有迹可循。',
  },
  {
    icon: Building2,
    title: '校区与经营',
    description: '汇总经营数据，按岗位与校区分配权限，适配机构成长。',
  },
];

const VERSION_META = {
  BASIC: { stage: '单校区起步', audience: '适合个人工作室与单校区机构' },
  STANDARD: { stage: '多校区成长', audience: '适合多校区连锁培训机构' },
  FRANCHISE: { stage: '加盟体系', audience: '适合品牌总部与加盟管理' },
};

function goToForm(event) {
  event.preventDefault();
  const isMobile = window.matchMedia?.('(max-width: 768px), (pointer: coarse)').matches;
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (!isMobile) document.getElementById('userName')?.focus({ preventScroll: true });
}

function HomePage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const sourceGroupCode = new URLSearchParams(window.location.search).get('sourceGroupCode');
  const [formData, setFormData] = useState({ userName: '', mobile: '', companyName: '' });
  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const [editions, setEditions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [versionsError, setVersionsError] = useState(null);
  const [versionsRetry, setVersionsRetry] = useState(0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    setVersionsLoading(true);
    setVersionsError(null);
    fetch(`${API_BASE_URL}/rest/subscription_intro/get`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((result) => {
        if (!active) return;
        if (result.code === '0' && result.data?.editions) {
          setEditions(result.data.editions);
        } else {
          setVersionsError(result.msg || '加载失败');
        }
      })
      .catch((error) => {
        if (!active) return;
        console.error('加载订阅版本失败', error);
        setVersionsError(controller.signal.aborted ? '请求超时，请重试' : '网络异常，请稍后重试');
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (active) setVersionsLoading(false);
      });
    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [versionsRetry]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const showMessage = (text, type) => setMessage({ text, type });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitLock.current) return;

    if (!/^1[3-9]\d{9}$/.test(formData.mobile)) {
      showMessage('请输入正确的中国大陆手机号码', 'error');
      return;
    }

    submitLock.current = true;
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/add_application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          ...(sourceGroupCode ? { sourceGroupCode } : {}),
        }),
      });
      const result = await response.json();
      if (result.code === '0') {
        showMessage('提交成功！我们会尽快与您联系。', 'success');
        setFormData({ userName: '', mobile: '', companyName: '' });
      } else {
        showMessage('提交失败，请稍后重试。', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('提交失败，请检查网络连接后重试。', 'error');
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const orderedEditions = () => {
    const editionMap = new Map(editions.map((edition) => [edition.edition, edition]));
    const seen = new Set();
    const hints = {
      STANDARD: '已包含基础版全部能力，并新增以下能力',
      FRANCHISE: '已包含标准版全部能力，并新增以下能力',
    };

    return ['BASIC', 'STANDARD', 'FRANCHISE'].map((key) => {
      const edition = editionMap.get(key);
      if (!edition) return null;
      const capabilities = (edition.capabilities || []).filter((capability) => {
        const capabilityKey = capability.id || capability.title;
        if (!capabilityKey || seen.has(capabilityKey)) return false;
        seen.add(capabilityKey);
        return true;
      });
      return { edition, key, capabilities, hint: hints[key] };
    });
  };

  return (
    <div
      className="marketing"
      onClick={(event) => {
        if (event.target.closest('a[href="#contact"]')) goToForm(event);
      }}
    >
      <header className="marketing-header">
        <div className="header-inner">
          <a className="brand" href="#top" aria-label="申嘻科技首页">
            <img className="brand-logo" src="/favicon.svg" alt="" width="34" height="34" />
            <span>申嘻科技</span>
          </a>
          <button
            className="menu-toggle"
            type="button"
            aria-label={menuOpen ? '关闭导航菜单' : '打开导航菜单'}
            aria-expanded={menuOpen}
            aria-controls="main-nav"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={21} /> : <Menu size={21} />}
          </button>
          <nav
            id="main-nav"
            className={menuOpen ? 'is-open' : ''}
            aria-label="页面导航"
            onClick={() => setMenuOpen(false)}
          >
            <a href="#education">申嘻教务</a>
            <a href="#music">音乐空间</a>
            <a href="#versions">订阅方案</a>
            <a className="management-entry" href="https://mc.sanesee.com/">
              机构管理端 <ArrowUpRight size={15} aria-hidden="true" />
            </a>
            <a className="primary header-cta" href="#contact">申请试用</a>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="marketing-hero" aria-labelledby="hero-title">
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow"><span className="eyebrow-dot" />音乐教育机构的数字化伙伴</p>
              <h1 id="hero-title">让教务更有序，<br /><em>让学习更专注。</em></h1>
              <p className="lead">
                以申嘻教务连接机构运营与学员服务，<br className="desktop-break" />
                以申嘻音乐空间延伸课堂之外的每一次学习。
              </p>
              <div className="actions">
                <a className="primary" href="#contact">
                  申请教务试用 <ArrowRight size={18} aria-hidden="true" />
                </a>
                <a className="secondary" href="#music">探索音乐空间</a>
              </div>
              <div className="hero-audience" aria-label="适用机构类型">
                <span>适用于</span>
                <strong>个人工作室</strong><i />
                <strong>单校区机构</strong><i />
                <strong>多校区连锁</strong>
              </div>
            </div>

            <aside className="hero-qr-panel" aria-label="微信扫码入口">
              <div className="hero-qr-heading">
                <span>微信扫码</span>
                <strong>连接申嘻产品与服务</strong>
              </div>
              <div className="hero-qr-entries">
                <figure className="hero-qr-entry official-account">
                  <a href="/images/marketing/wechat-official-qrcode.jpg" aria-label="查看申嘻教务公众号二维码原图">
                    <img src="/images/marketing/wechat-official-qrcode.jpg" alt="申嘻教务官方公众号二维码" width="160" height="160" />
                  </a>
                  <figcaption><h2>申嘻教务</h2><span>官方公众号</span><p>了解教务服务与产品动态</p></figcaption>
                </figure>
                <figure className="hero-qr-entry">
                  <a href="/images/marketing/music-space-qrcode.jpg" aria-label="查看申嘻音乐空间小程序码原图">
                    <img src="/images/marketing/music-space-qrcode.jpg" alt="申嘻音乐空间微信小程序码" width="160" height="160" />
                  </a>
                  <figcaption><h2>申嘻音乐空间</h2><span>微信小程序</span><p>扫码开始音乐学习与练习</p></figcaption>
                </figure>
              </div>
            </aside>
          </div>
        </section>

        <div className="product-index" aria-label="核心产品">
          <div className="container product-index-grid">
            <a href="#education">
              <span>01</span>
              <div><strong>申嘻教务</strong><p>机构运营与学员服务</p></div>
              <ArrowRight size={20} aria-hidden="true" />
            </a>
            <a href="#music">
              <span>02</span>
              <div><strong>申嘻音乐空间</strong><p>音乐学习与自主练习</p></div>
              <ArrowRight size={20} aria-hidden="true" />
            </a>
          </div>
        </div>

        <section className="education-section" id="education" aria-labelledby="education-title">
          <div className="container education-intro">
            <header className="section-heading">
              <div>
                <p className="eyebrow">01 / 申嘻教务</p>
                <h2 id="education-title">一套系统，<br />理清机构每一天</h2>
              </div>
              <p className="section-intro">
                围绕招生、排课、课消与学员服务，把分散的日常工作放进同一套流程，让管理者、员工与学员各取所需。
              </p>
            </header>

            <div className="management-layout" id="features">
              <div className="management-grid">
                {management.map(({ icon: Icon, title, description }, index) => (
                  <article key={title}>
                    <div className="feature-topline">
                      <Icon size={23} aria-hidden="true" />
                      <span>0{index + 1}</span>
                    </div>
                    <h3>{title}</h3>
                    <p>{description}</p>
                  </article>
                ))}
              </div>
              <figure className="product-preview employee-preview">
                <div className="preview-label"><span>员工端</span><strong>工作</strong></div>
                <div className="device-frame">
                  <img src="/images/marketing/employee-work.png" alt="员工端工作页：招生管理与教务管理功能入口" width="390" height="844" loading="lazy" />
                </div>
                <figcaption><Smartphone size={15} aria-hidden="true" />移动办公，随时处理机构事务</figcaption>
              </figure>
            </div>

            <div className="student-panel" id="students">
              <figure className="student-preview">
                <div className="device-frame">
                  <img src="/images/marketing/student-home.png" alt="学员首页：课程预约、音乐空间与上课安排" width="390" height="844" loading="lazy" />
                </div>
                <figcaption>学员端 · 首页</figcaption>
              </figure>
              <div className="student-copy">
                <p className="eyebrow">学员服务 / 学员端</p>
                <h3>课堂之外，<br />服务仍在继续</h3>
                <p>把课程安排与学习资源送到学员手中，让机构服务从校内自然延伸到课后。</p>
                <ul className="student-services">
                  <li><Check size={17} aria-hidden="true" />预约课程，安排学习时间</li>
                  <li><Check size={17} aria-hidden="true" />查看课程与上课安排</li>
                  <li><Check size={17} aria-hidden="true" />获取资源，继续课后学习</li>
                </ul>
                <a className="text-link" href="#music">
                  了解申嘻音乐空间 <ArrowRight size={16} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>

          <section className="versions-section" id="versions" aria-labelledby="versions-title">
            <div className="container">
              <header className="section-heading versions-heading">
                <div>
                  <p className="eyebrow">订阅方案 / 申嘻教务</p>
                  <h2 id="versions-title">匹配机构当下，<br />支持下一阶段成长</h2>
                </div>
                <p className="section-intro">从个人工作室到连锁机构，按校区数量与所需能力选择适合的订阅方案。</p>
              </header>

              <div className="versions-commercial" aria-label="订阅定价与优惠说明">
                <div>
                  <span>灵活定价</span>
                  <strong>按校区数量与购买年限综合定价</strong>
                  <p>校区越多、购买年限越长，单校区价格越优惠。</p>
                </div>
                <div className="versions-promotion">
                  <span>限时优惠</span>
                  <strong>买 2 年送 1 年 · 买 3 年送 2 年</strong>
                  <p>优惠具有时效性，具体以咨询时的有效方案为准。</p>
                </div>
              </div>

              {versionsLoading && <div className="versions-empty" role="status">加载中…</div>}
              {!versionsLoading && versionsError && (
                <div className="versions-empty error" role="status">
                  订阅信息暂时无法加载，不影响申请试用。
                  <button type="button" onClick={() => setVersionsRetry((value) => value + 1)}>重试</button>
                </div>
              )}
              {!versionsLoading && !versionsError && editions.length === 0 && (
                <div className="versions-empty">暂无版本信息</div>
              )}
              {!versionsLoading && !versionsError && editions.length > 0 && (
                <div className="versions-grid">
                  {orderedEditions().map((item) => {
                    if (!item) return null;
                    const { edition, key, capabilities, hint } = item;
                    const recommended = edition.recommendFlag === 1;
                    const meta = VERSION_META[key];
                    return (
                      <article key={edition.id || key} className={recommended ? 'version-card recommended' : 'version-card'}>
                        <header className="version-heading">
                          <div className="version-labels">
                            <span className="version-stage">{meta.stage}</span>
                            {recommended && <span className="ribbon">推荐</span>}
                          </div>
                          <h3 className="version-name">{edition.displayName}</h3>
                          <p className="version-subtitle">{edition.subtitle || meta.audience}</p>
                        </header>
                        <ul className="capability-list">
                          {hint && <li className="capability-hint">{hint}</li>}
                          {capabilities.map((capability) => (
                            <li className="capability-item" key={capability.id || capability.title}>
                              <span className="check-icon"><Check size={15} strokeWidth={2.5} aria-hidden="true" /></span>
                              <div>
                                <strong>{capability.title}</strong>
                                {capability.description && <p>{capability.description}</p>}
                              </div>
                            </li>
                          ))}
                          {capabilities.length === 0 && <li className="capability-empty">暂无额外能力</li>}
                        </ul>
                        <a href="#contact" className="btn-cta">申请教务试用 <ArrowRight size={16} aria-hidden="true" /></a>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </section>

        <section className="music-section" id="music" aria-labelledby="music-title">
          <div className="container music-grid">
            <div className="music-copy">
              <p className="eyebrow">02 / 申嘻音乐空间</p>
              <h2 id="music-title">把课堂延伸到<br />每一次自主练习</h2>
              <p className="music-intro">一款独立的音乐学习微信小程序，将系统学习、基础训练与实用工具放在同一个空间。</p>
              <div className="learning-list">
                {learningGroups.map(({ icon: Icon, title, features, description }) => (
                  <article key={title}>
                    <Icon size={22} aria-hidden="true" />
                    <div><h3>{title}</h3><strong>{features}</strong><p>{description}</p></div>
                  </article>
                ))}
              </div>
            </div>
            <figure className="music-device">
              <span className="music-orbit" aria-hidden="true"><Music2 size={24} /></span>
              <div className="device-frame">
                <img src="/images/marketing/music-space.png" alt="音乐空间微信小程序首页：课程学习、基础练习与实用工具" width="750" height="1622" loading="lazy" />
              </div>
              <figcaption>申嘻音乐空间 · 微信小程序</figcaption>
            </figure>
          </div>
        </section>

        <section className="contact" id="contact" aria-labelledby="contact-title">
          <div className="container contact-grid">
            <div className="contact-info">
              <p className="eyebrow">教务试用与咨询</p>
              <h2 id="contact-title">从一次沟通开始，<br />找到合适的教务方案</h2>
              <p className="contact-intro">留下机构信息，我们将与您沟通管理需求，介绍匹配的申嘻教务功能与订阅方案。</p>
              <ol className="contact-steps">
                <li><span>01</span>了解机构规模与当前流程</li>
                <li><span>02</span>介绍与您需求匹配的功能</li>
                <li><span>03</span>确认适合的试用与订阅方案</li>
              </ol>
            </div>

            <div className="contact-form-wrapper">
              <div className="contact-form">
                <h3>申请教务试用</h3>
                <p className="contact-form-intro">填写以下信息，我们会尽快与您联系。</p>
                {message && <div className={`message ${message.type}`} role="alert">{message.text}</div>}
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="userName">您的姓名</label>
                    <input type="text" id="userName" name="userName" placeholder="请输入您的姓名" required autoComplete="name" value={formData.userName} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mobile">联系电话</label>
                    <input type="tel" id="mobile" name="mobile" placeholder="请输入手机号码" required autoComplete="tel" inputMode="numeric" value={formData.mobile} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="companyName">机构名称</label>
                    <input type="text" id="companyName" name="companyName" placeholder="请输入培训机构名称" required autoComplete="organization" value={formData.companyName} onChange={handleInputChange} />
                  </div>
                  <button type="submit" className="primary submit-button" disabled={submitting}>
                    {submitting ? '提交中…' : '提交试用申请'}
                    {!submitting && <ArrowRight size={17} aria-hidden="true" />}
                  </button>
                </form>
                <p className="privacy">信息仅用于试用申请与顾问联系。</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="marketing-footer">
        <div className="container footer-main">
          <div className="footer-brand">
            <a className="brand" href="#top"><img className="brand-logo" src="/favicon.svg" alt="" width="32" height="32" /><span>申嘻科技</span></a>
            <p>连接机构管理与学员学习</p>
          </div>
          <nav aria-label="页脚导航">
            <a href="#education">申嘻教务</a>
            <a href="#music">音乐空间</a>
            <a href="#versions">订阅方案</a>
            <a href="#contact">申请试用</a>
          </nav>
        </div>
        <div className="container footer-bottom">
          <p>© {new Date().getFullYear()} 深圳申嘻科技有限公司</p>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备19098413号-2</a>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
